import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Badge, Tooltip, Modal } from 'antd';
import {
  MessageOutlined,
  CloseOutlined,
  SendOutlined,
  RobotOutlined,
  UserOutlined,
  QuestionCircleOutlined,
  ExpandOutlined,
  CompressOutlined,
} from '@ant-design/icons';
import { chatbotAPI } from '../services/api';
import { color, radius, shadow, font, space } from '../theme';

interface ChatMessage {
  id: number;
  role: 'user' | 'bot';
  content: string;
  time: string;
}

const QUICK_ACTIONS = [
  { label: '현황', value: '현황' },
  { label: '일정 현황', value: '일정 현황' },
  { label: '계획서 현황', value: '계획서 현황' },
  { label: '의뢰사', value: '의뢰사' },
  { label: '최근 활동', value: '최근 활동' },
  { label: '오늘 일정', value: '오늘 일정' },
  { label: '인기 검색어', value: '인기 검색어' },
  { label: '메뉴 안내', value: '메뉴 안내' },
];

// Claude AI(Sonnet) 자연어 검색 활용 예시 — 실데이터로 답변 가능한 질문만 노출
const AI_EXAMPLES = [
  // 주의: '검색/찾아줘/현황/요약' 등 규칙 인텐트 키워드가 포함되면 AI가 아닌 규칙 응답으로 빠짐
  // 단 '무엇을 할 수 있어?'는 의도적으로 규칙(assistant_role)에 태워 답을 고정한다 — 역할 설명은 흔들리면 안 된다
  '무엇을 할 수 있어?',
  '수박 역병 시험은 어떻게 진행되고 있어?',
  '복숭아 탄저병 시험이 지금 어느 단계야?',
  '성적서 작성중인 시험 알려줘',
  '살균제 약효시험 어떤 게 있어?',
  '양파 관련 시험 진행 상황은?',
];

const AI_BADGE_KEY = 'sappt_ai_badge_seen_v1'; // AI 신규 적용 알림 배지 — 챗봇 1회 오픈 시 해제

// AI 추천 질문 50 — 실데이터(작물·병해충·의뢰사·상태·지역·약제) 기반, 담당 업무별 5그룹
// 주의: '검색/찾아줘/요약/통계/일정 현황' 등 규칙 인텐트 키워드를 쓰면 AI가 아닌 규칙 응답으로 빠지므로 회피
const AI_QUESTION_GROUPS: { icon: string; title: string; desc: string; questions: string[] }[] = [
  {
    icon: '💬', title: '도우미 사용법', desc: '처음 쓰는 분',
    questions: [
      '무엇을 할 수 있어?',
      '어떤 걸 물어보면 돼?',
      '네 역할이 뭐야?',
      '어떻게 쓰는 거야?',
      '메뉴 안내',
      '도움말',
    ],
  },
  {
    icon: '🧪', title: '시험 진행·단계 확인', desc: '시험 담당 연구원',
    questions: [
      '수박 역병 시험은 지금 어느 단계야?',
      '복숭아 탄저병 시험 진행 상황 알려줘',
      '사과 사과굴나방 시험은 어떻게 되고 있어?',
      '양파 녹병 시험 몇 건 진행 중이야?',
      '잔디 피티움마름병 시험 상태 알려줘',
      '오이 흰가루병 시험 완료됐어?',
      '배 화상병 약해시험은 어느 단계야?',
      '파밤나방 방제 시험 어떤 게 있어?',
      '미국선녀벌레 시험 진행 상태 알려줘',
      '감자 복숭아혹진딧물 시험 어떻게 진행되고 있어?',
    ],
  },
  {
    icon: '📋', title: '상태·기간별 파악', desc: '시험 책임자·관리자',
    questions: [
      '성적서 작성중인 시험 알려줘',
      '아직 시작 전인 살균 시험 몇 건이야?',
      '조사완료 상태인 시험 중 협회 의뢰 건은?',
      '발급완료된 시험 목록 알려줘',
      '처리완료 단계에 머물러 있는 시험 있어?',
      '올해 살충 시험 얼마나 진행됐어?',
      '제초제 시험은 몇 건이고 어느 단계야?',
      '기초시험 진행 상태 알려줘',
      '성적서완료인데 아직 발급 안 된 시험 있어?',
      '26년 시험 중 완료된 건 알려줘',
    ],
  },
  {
    icon: '🏢', title: '의뢰사별 진행 파악', desc: '의뢰사 대응 담당',
    questions: [
      '팜한농 의뢰 시험 진행 상태 알려줘',
      '농협 시험 중 성적서작성중인 건 뭐야?',
      '태준 의뢰 시험 어떤 게 있어?',
      '경농 시험 진행 상태가 궁금해',
      '협회 경유 시험 몇 건 진행 중이야?',
      'ISK 의뢰 시험 알려줘',
      '한국삼공 시험 진행 단계는?',
      '니시노코리아 의뢰 건 상태 알려줘',
      '에스비성보 시험 어떻게 되고 있어?',
      '협회 의뢰 중 조사완료된 시험 알려줘',
    ],
  },
  {
    icon: '📝', title: '계획서·약제 확인', desc: '계획서 작성·검토 담당',
    questions: [
      '벼 관련 계획서 어떤 게 있어?',
      '사과 잿빛무늬병 계획서 알려줘',
      '딸기 흰가루병 계획서 있어?',
      'Metconazole 약제 들어간 계획서 알려줘',
      '포도 계획서 목록이 궁금해',
      '토마토 계획서는 어떤 시험이야?',
      '감귤 관련 계획서와 시험 알려줘',
      'Fluxapyroxad 쓰는 시험 있어?',
      '참다래 세균꽃썩음병 시험 계획 알려줘',
      '잿빛곰팡이병 계획서 몇 건이야?',
    ],
  },
  {
    icon: '📍', title: '지역·포장별 확인', desc: '현장·운영 담당',
    questions: [
      '경기 지역에서 진행 중인 시험 알려줘',
      '충북에서 하는 살균 시험 있어?',
      '제주 지역 시험 어떤 게 있어?',
      '전남에서 진행되는 시험 상태는?',
      '강원 지역 시험 몇 건이야?',
      '용인에서 하는 시험 알려줘',
      '협회 의뢰 중 경기 지역 시험 있어?',
      '올해 경북 지역 시험 진행 상태 알려줘',
      '충남 논산 쪽 시험 뭐가 있어?',
      '배추 밑둥썩음병 시험은 어디서 해?',
    ],
  },
  {
    icon: '🐛', title: '병해충정보 (NCPMS 예찰)', desc: '병해충 예찰·방제 담당',
    questions: [
      '고추에 최근 발생 많은 병해충 알려줘',
      '논벼 벼멸구 발생 추세 어때?',
      '사과 병해충 예찰 결과 알려줘',
      '배추에 지금 주의해야 할 병해충 있어?',
      '복숭아 예찰에서 발생 많은 병해충은 뭐야?',
      '마늘 병해충 발생 상태 알려줘',
      '감귤 병해충 예찰 결과가 궁금해',
      '무 병해충 최근 발생 알려줘',
      '배 과수 병해충 발생 상태는 어때?',
      '보리 병해충 예찰 발생 정보 알려줘',
    ],
  },
  {
    icon: '🌡️', title: 'TAPO 시설 모니터링', desc: '시설·설비 관리 담당',
    questions: [
      '시약실 온도 지금 몇 도야?',
      '사육실 온습도 상태 알려줘',
      '문서보관실 습도 괜찮아?',
      '시설 온습도 전체 상태 알려줘',
      '센서 배터리 부족한 방 있어?',
      '사육실 온도가 너무 높지 않아?',
      '시약실 습도 상태 확인해줘',
      '문서보관실 온도 알려줘',
    ],
  },
];

const AI_QUESTION_COUNT = AI_QUESTION_GROUPS.reduce((n, g) => n + g.questions.length, 0);

// AI 챗봇 전용 팔레트 — 본체(인디고 #4f46e5)와 구분되는 바이올렛 계열로 'AI 공간' 식별성 부여
const AI_THEME = {
  primary: '#7c3aed',
  gradient: 'linear-gradient(135deg, #6d28d9 0%, #9333ea 55%, #c026d3 100%)',
  headerGradient: 'linear-gradient(135deg, #2e1065 0%, #5b21b6 60%, #7c3aed 100%)',
  avatarGradient: 'linear-gradient(135deg, #7c3aed, #c026d3)',
  sendGradient: 'linear-gradient(135deg, #6d28d9, #9333ea)',
  soft: '#faf5ff',
  border: '#ddd6fe',
  shadow: '0 8px 28px rgba(109, 40, 217, 0.35)',
};

const ChatBot: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([
    {
      id: 0,
      role: 'bot',
      content: '안녕하세요! SAPPT 도우미입니다.\n✨ AI 기능이 개선되어 자연어 질문의 이해와 답변 정확도가 크게 향상되었습니다.\n아래 AI 검색 예시를 눌러보거나, 편하게 문장으로 질문해 주세요.',
      time: new Date().toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit', hour12: false }),
    },
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [hasNew, setHasNew] = useState(false);
  const [showGuide, setShowGuide] = useState(false);
  const [aiBadge, setAiBadge] = useState(() => {
    try { return localStorage.getItem(AI_BADGE_KEY) !== '1'; } catch { return true; }
  });
  const [showMore, setShowMore] = useState(false); // 추천 질문 50 모달
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  let msgId = useRef(1);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  useEffect(() => {
    if (open) {
      setHasNew(false);
      if (aiBadge) {
        setAiBadge(false);
        try { localStorage.setItem(AI_BADGE_KEY, '1'); } catch { /* ignore */ }
      }
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [open, aiBadge]);

  const sendMessage = async (text: string) => {
    if (!text.trim() || loading) return;

    const now = new Date().toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit', hour12: false });
    const userMsg: ChatMessage = { id: msgId.current++, role: 'user', content: text.trim(), time: now };
    setMessages((prev) => [...prev, userMsg]);
    setInput('');
    setLoading(true);

    // 스트리밍 응답용 bot 메시지 미리 추가
    const botId = msgId.current++;
    const botTime = new Date().toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit', hour12: false });
    setMessages((prev) => [...prev, { id: botId, role: 'bot', content: '', time: botTime }]);
    let accumulated = '';

    try {
      await chatbotAPI.stream(text.trim(), {
        onChunk: (t) => {
          accumulated += t;
          setMessages((prev) => prev.map((m) => (m.id === botId ? { ...m, content: accumulated } : m)));
        },
        onReply: (t) => {
          accumulated = t;
          setMessages((prev) => prev.map((m) => (m.id === botId ? { ...m, content: accumulated } : m)));
        },
        onError: () => {
          setMessages((prev) => prev.map((m) => (m.id === botId ? { ...m, content: '죄송합니다. 요청 처리 중 오류가 발생했습니다.' } : m)));
        },
      });
      if (!open) setHasNew(true);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage(input);
    }
  };

  return (
    <>
      {/* Floating Button */}
      <div
        onClick={() => setOpen(!open)}
        style={{
          position: 'fixed', bottom: space.lg, right: space.lg, zIndex: 1000,
          width: 56, height: 56, borderRadius: radius.full,
          background: AI_THEME.gradient,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          cursor: 'pointer', boxShadow: AI_THEME.shadow,
          transition: 'all 0.3s ease',
        }}
        onMouseEnter={(e) => { e.currentTarget.style.transform = 'scale(1.1)'; }}
        onMouseLeave={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}
      >
        <Badge dot={hasNew} offset={[-2, 2]}>
          {open ? (
            <CloseOutlined style={{ fontSize: 22, color: '#fff' }} />
          ) : (
            <MessageOutlined style={{ fontSize: 22, color: '#fff' }} />
          )}
        </Badge>
        {/* AI 신규 적용 알림 배지 — 첫 오픈 전까지 표시 */}
        {aiBadge && !open && (
          <span style={{
            position: 'absolute', top: -10, left: -14,
            padding: '2px 8px', borderRadius: 10,
            background: 'linear-gradient(135deg, #7c3aed, #4f46e5)',
            color: '#fff', fontSize: 10, fontWeight: 800, letterSpacing: 0.5,
            boxShadow: '0 2px 8px rgba(124,58,237,0.45)',
            animation: 'aiBadgePulse 1.6s ease-in-out infinite',
            whiteSpace: 'nowrap', pointerEvents: 'none',
          }}>
            AI NEW
          </span>
        )}
      </div>

      {/* Chat Window */}
      {open && (
        <div style={{
          position: 'fixed', bottom: 92, right: space.lg, zIndex: 999,
          width: expanded ? 'min(760px, calc(100vw - 48px))' : 'min(440px, calc(100vw - 48px))',
          height: expanded ? 'calc(100vh - 140px)' : 'min(620px, calc(100vh - 140px))',
          borderRadius: radius.xl,
          background: color.bg, boxShadow: shadow.xl,
          display: 'flex', flexDirection: 'column', overflow: 'hidden',
          border: `1px solid ${AI_THEME.border}`,
          animation: 'chatSlideUp 0.3s ease',
        }}>
          {/* Header — AI 전용 딥 바이올렛 (본체 인디고와 구분) */}
          <div style={{
            background: AI_THEME.headerGradient,
            padding: `${space.base}px ${space.lg - 4}px`, display: 'flex', alignItems: 'center', gap: space.md,
          }}>
            <div style={{
              width: 36, height: 36, borderRadius: '50%',
              background: 'rgba(255,255,255,0.2)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <RobotOutlined style={{ fontSize: 20, color: '#fff' }} />
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ color: color.textOnPrimary, fontWeight: font.weight.bold, fontSize: font.size.md, display: 'flex', alignItems: 'center', gap: 6 }}>
                SAPPT 도우미
                <span style={{
                  padding: '1px 7px', borderRadius: 8, background: 'rgba(255,255,255,0.22)',
                  fontSize: 10, fontWeight: 700, letterSpacing: 0.4,
                }}>
                  ✨ AI
                </span>
              </div>
              <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: font.size.xs }}>AI 자연어 검색 · 시스템 현황 조회</div>
            </div>
            <Tooltip title={expanded ? '축소' : '확대'}>
              {expanded ? (
                <CompressOutlined
                  style={{ color: 'rgba(255,255,255,0.85)', fontSize: 16, cursor: 'pointer', marginRight: 4 }}
                  onClick={() => setExpanded(false)}
                />
              ) : (
                <ExpandOutlined
                  style={{ color: 'rgba(255,255,255,0.85)', fontSize: 16, cursor: 'pointer', marginRight: 4 }}
                  onClick={() => setExpanded(true)}
                />
              )}
            </Tooltip>
            <Tooltip title="도움말">
              <QuestionCircleOutlined
                style={{ color: 'rgba(255,255,255,0.7)', fontSize: 16, cursor: 'pointer' }}
                onClick={() => sendMessage('도움말')}
              />
            </Tooltip>
          </div>

          {/* Messages */}
          <div style={{
            flex: 1, overflowY: 'auto', padding: space.base,
            background: AI_THEME.soft,
          }}>
            {messages.map((msg) => (
              <div
                key={msg.id}
                style={{
                  display: 'flex', flexDirection: msg.role === 'user' ? 'row-reverse' : 'row',
                  marginBottom: 12, gap: 8, alignItems: 'flex-start',
                }}
              >
                {/* Avatar */}
                <div style={{
                  width: 30, height: 30, borderRadius: '50%', flexShrink: 0,
                  background: msg.role === 'bot'
                    ? AI_THEME.avatarGradient
                    : 'linear-gradient(135deg, #64748b, #94a3b8)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  {msg.role === 'bot'
                    ? <RobotOutlined style={{ color: '#fff', fontSize: 14 }} />
                    : <UserOutlined style={{ color: '#fff', fontSize: 14 }} />}
                </div>

                {/* Bubble */}
                <div style={{
                  maxWidth: msg.role === 'bot' ? '92%' : '78%',
                  padding: `${space.md - 2}px ${space.md + 2}px`, borderRadius: radius.lg,
                  background: msg.role === 'user' ? AI_THEME.primary : color.bg,
                  color: msg.role === 'user' ? color.textOnPrimary : color.text,
                  fontSize: font.size.base, lineHeight: font.lineHeight.base, whiteSpace: 'pre-wrap',
                  fontFamily: msg.role === 'bot' ? font.mono : 'inherit',
                  wordBreak: 'break-word',
                  boxShadow: msg.role === 'bot' ? shadow.md : 'none',
                  borderTopLeftRadius: msg.role === 'bot' ? 4 : radius.lg,
                  borderTopRightRadius: msg.role === 'user' ? 4 : radius.lg,
                }}>
                  {msg.content}
                  <div style={{
                    fontSize: 10, marginTop: 4, textAlign: 'right',
                    color: msg.role === 'user' ? 'rgba(255,255,255,0.6)' : '#94a3b8',
                  }}>
                    {msg.time}
                  </div>
                </div>
              </div>
            ))}

            {loading && (
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 12 }}>
                <div style={{
                  width: 30, height: 30, borderRadius: '50%', flexShrink: 0,
                  background: AI_THEME.avatarGradient,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <RobotOutlined style={{ color: '#fff', fontSize: 14 }} />
                </div>
                <div style={{
                  padding: '10px 14px', borderRadius: 12, borderTopLeftRadius: 4,
                  background: '#fff', boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
                }}>
                  <div style={{ display: 'flex', gap: 4 }}>
                    <span className="chat-dot" style={{ animationDelay: '0s' }} />
                    <span className="chat-dot" style={{ animationDelay: '0.2s' }} />
                    <span className="chat-dot" style={{ animationDelay: '0.4s' }} />
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Quick Actions */}
          <div style={{
            padding: '8px 16px', background: '#fff',
            borderTop: '1px solid #f1f5f9',
            display: 'flex', gap: 6, flexWrap: 'wrap',
          }}>
            {QUICK_ACTIONS.map((qa) => (
              <button
                key={qa.value}
                onClick={() => sendMessage(qa.value)}
                disabled={loading}
                style={{
                  padding: '4px 10px', borderRadius: 12,
                  border: '1px solid #e2e8f0', background: '#f8fafc',
                  fontSize: 11, color: '#475569', cursor: 'pointer',
                  transition: 'all 0.2s',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = '#f5f3ff';
                  e.currentTarget.style.borderColor = '#ddd6fe';
                  e.currentTarget.style.color = '#7c3aed';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = '#f8fafc';
                  e.currentTarget.style.borderColor = '#e2e8f0';
                  e.currentTarget.style.color = '#475569';
                }}
              >
                {qa.label}
              </button>
            ))}
          </div>

          {/* AI 검색 활용 예시 — 입력창 위 상시 노출 */}
          <div style={{
            padding: '8px 16px 4px', background: 'linear-gradient(135deg, #f5f3ff 0%, #eef2ff 100%)',
            borderTop: '1px solid #e0e7ff',
          }}>
            <div style={{
              fontSize: 11, fontWeight: 700, color: '#6d28d9', marginBottom: 6,
              display: 'flex', alignItems: 'center', gap: 4,
            }}>
              ✨ AI 검색 활용 예시 <span style={{ fontWeight: 400, color: '#8b5cf6' }}>— 문장으로 물어보세요</span>
              <button className="ai-more-btn" onClick={() => setShowMore(true)}>
                💡 추천질문 <span className="ai-more-count">{AI_QUESTION_COUNT}</span> 더보기
              </button>
            </div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', paddingBottom: 4 }}>
              {AI_EXAMPLES.map((ex) => (
                <button
                  key={ex}
                  onClick={() => sendMessage(ex)}
                  disabled={loading}
                  style={{
                    padding: '4px 11px', borderRadius: 13,
                    border: '1px solid #c7d2fe', background: '#fff',
                    fontSize: 11, color: '#4f46e5', cursor: 'pointer',
                    transition: 'all 0.2s', fontWeight: 500,
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = '#4f46e5';
                    e.currentTarget.style.color = '#fff';
                    e.currentTarget.style.borderColor = '#4f46e5';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = '#fff';
                    e.currentTarget.style.color = '#4f46e5';
                    e.currentTarget.style.borderColor = '#c7d2fe';
                  }}
                >
                  {ex}
                </button>
              ))}
            </div>
          </div>

          {/* 사용 예시 가이드 (토글) */}
          {showGuide && (
            <div id="usage-examples-panel" style={{
              padding: '14px 16px', borderTop: '1px solid #e5e7eb',
              background: 'linear-gradient(135deg, #eef2ff 0%, #f0f9ff 100%)', fontSize: 12, color: '#1e293b',
              maxHeight: 340, overflowY: 'auto',
            }}>
              <div style={{ fontWeight: 700, marginBottom: 10, fontSize: 13, color: '#4338ca', display: 'flex', alignItems: 'center', gap: 6 }}>
                {'💡'} 가능 질문 & 예시 답변
              </div>

              {[
                { q: '현황', a: '📊 시험계획서 N건, 시험일정 N건, 의뢰사 N개, 시험장소 N개 등 전체 시스템 요약' },
                { q: '일정 현황', a: '📋 시험일정 총 건수 + 상태별(진행중/완료/예정) · 연도별 집계' },
                { q: '계획서 현황', a: '📝 시험계획서 총 건수 + 기관별 등록 현황' },
                { q: '일정 검색 수박', a: '🔍 "수박" 관련 시험일정 검색\n  예) [진행중] SR2401-001 - 수박 약효시험 (○○농약)' },
                { q: '계획서 검색 벼', a: '🔍 "벼" 관련 계획서 검색\n  예) P2401-003 - 벼 제초제 효과시험 (벼)' },
                { q: '검색 SR22', a: '🔍 시험번호/키워드로 일정+계획서 통합 검색' },
                { q: '의뢰사', a: '🏢 전체 의뢰사 목록 + 각 의뢰사별 시험 건수 표시' },
                { q: '오늘 일정', a: '📅 현재 진행 중인 시험일정 목록 표시' },
                { q: '최근 활동', a: '📜 최근 활동이력 (로그인/생성/수정/삭제 등)' },
                { q: '인기 검색어', a: '🔥 최근 7일간 인기 검색 키워드 순위' },
                { q: '메뉴 안내', a: '시스템 전체 메뉴 구조와 각 기능 설명 안내' },
                { q: '진행중인 시험은 몇 건이야?', a: '🤖 AI가 자연어를 이해하여 데이터 기반 답변 제공' },
              ].map((item, idx) => (
                <div key={idx} style={{
                  marginBottom: 8, padding: '8px 12px', borderRadius: 8,
                  background: '#fff', border: '1px solid #e0e7ff',
                  cursor: 'pointer', transition: 'all 0.2s',
                }}
                  onClick={() => { setShowGuide(false); sendMessage(item.q); }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = '#818cf8';
                    e.currentTarget.style.background = '#f5f3ff';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = '#e0e7ff';
                    e.currentTarget.style.background = '#fff';
                  }}
                >
                  <div style={{ fontWeight: 600, color: '#4f46e5', fontSize: 12, marginBottom: 3 }}>
                    Q. {item.q}
                  </div>
                  <div style={{ color: '#475569', fontSize: 11, lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
                    {item.a}
                  </div>
                </div>
              ))}

              <div style={{ marginTop: 8, padding: '6px 10px', borderRadius: 6, background: 'rgba(99,102,241,0.08)', fontSize: 11, color: '#6366f1', lineHeight: 1.5 }}>
                <b>Tip:</b> 위 질문을 클릭하면 바로 실행됩니다. 자연어 질문도 자유롭게 입력해보세요!
              </div>
            </div>
          )}

          {/* Input */}
          <div style={{
            padding: '12px 16px', borderTop: '1px solid #e5e7eb',
            display: 'flex', gap: 8, background: '#fff',
          }}>
            <Tooltip title={showGuide ? '사용 예시 접기' : '사용 예시 보기'}>
              <button
                onClick={() => setShowGuide((v) => !v)}
                style={{
                  width: 40, height: 40, borderRadius: 10,
                  border: '1px solid #e2e8f0', background: showGuide ? '#eef2ff' : '#f8fafc',
                  color: showGuide ? '#4f46e5' : '#64748b',
                  cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  transition: 'all 0.2s',
                }}
              >
                <QuestionCircleOutlined style={{ fontSize: 16 }} />
              </button>
            </Tooltip>
            <input
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="메시지를 입력하세요..."
              disabled={loading}
              style={{
                flex: 1, padding: '10px 14px', borderRadius: 10,
                border: '1px solid #e2e8f0', outline: 'none',
                fontSize: 13, background: '#f8fafc',
                transition: 'border-color 0.2s',
              }}
              onFocus={(e) => { e.target.style.borderColor = '#c4b5fd'; }}
              onBlur={(e) => { e.target.style.borderColor = '#e2e8f0'; }}
            />
            <button
              onClick={() => sendMessage(input)}
              disabled={loading || !input.trim()}
              style={{
                width: 40, height: 40, borderRadius: 10,
                border: 'none',
                background: input.trim() ? AI_THEME.sendGradient : '#e2e8f0',
                color: input.trim() ? '#fff' : '#94a3b8',
                cursor: input.trim() ? 'pointer' : 'default',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                transition: 'all 0.2s',
              }}
            >
              <SendOutlined style={{ fontSize: 16 }} />
            </button>
          </div>
        </div>
      )}

      {/* 추천 질문 50 모달 — 담당 업무별 그룹 (실데이터 기반) */}
      <Modal
        open={showMore}
        onCancel={() => setShowMore(false)}
        footer={null}
        width={720}
        zIndex={1100}
        title={
          <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 16 }}>💡</span>
            <span style={{ fontWeight: 700 }}>AI 추천 질문 {AI_QUESTION_COUNT}</span>
            <span style={{ fontSize: 12, fontWeight: 400, color: '#8b5cf6' }}>
              담당 업무별로 골라 클릭하면 바로 질문됩니다
            </span>
          </span>
        }
      >
        <div style={{ maxHeight: '62vh', overflowY: 'auto', paddingRight: 4 }}>
          {AI_QUESTION_GROUPS.map((g) => (
            <div key={g.title} style={{ marginBottom: 18 }}>
              <div style={{
                display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8,
                padding: '6px 10px', borderRadius: 8,
                background: 'linear-gradient(135deg, #f5f3ff, #eef2ff)',
                border: '1px solid #e0e7ff',
              }}>
                <span style={{ fontSize: 15 }}>{g.icon}</span>
                <span style={{ fontWeight: 700, fontSize: 13, color: '#4c1d95' }}>{g.title}</span>
                <span style={{ fontSize: 11, color: '#8b5cf6' }}>· {g.desc}</span>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
                {g.questions.map((q) => (
                  <button
                    key={q}
                    onClick={() => { setShowMore(false); if (!open) setOpen(true); sendMessage(q); }}
                    disabled={loading}
                    style={{
                      textAlign: 'left', padding: '7px 11px', borderRadius: 9,
                      border: '1px solid #ede9fe', background: '#fff',
                      fontSize: 12, color: '#374151', cursor: 'pointer',
                      transition: 'all 0.15s', lineHeight: 1.4,
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = '#f5f3ff';
                      e.currentTarget.style.borderColor = '#c4b5fd';
                      e.currentTarget.style.color = '#6d28d9';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = '#fff';
                      e.currentTarget.style.borderColor = '#ede9fe';
                      e.currentTarget.style.color = '#374151';
                    }}
                  >
                    {q}
                  </button>
                ))}
              </div>
            </div>
          ))}
          <div style={{ padding: '8px 12px', borderRadius: 8, background: 'rgba(124,58,237,0.06)', fontSize: 11, color: '#7c3aed', lineHeight: 1.6 }}>
            <b>Tip:</b> 작물·병해충·의뢰사·지역·상태를 조합해 자유롭게 문장으로 질문할 수 있습니다.
            예) "팜한농 의뢰 중 경기 지역에서 조사완료된 살균 시험 알려줘"
          </div>
        </div>
      </Modal>

      {/* CSS Animation */}
      <style>{`
        @keyframes chatSlideUp {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .ai-more-btn {
          margin-left: auto;
          display: inline-flex; align-items: center; gap: 4px;
          padding: 3px 12px; border-radius: 12px; border: none; cursor: pointer;
          background: linear-gradient(90deg, #6d28d9, #9333ea, #c026d3, #9333ea, #6d28d9);
          background-size: 300% 100%;
          color: #fff; font-size: 11px; font-weight: 700; letter-spacing: 0.2px;
          box-shadow: 0 2px 8px rgba(124, 58, 237, 0.35);
          animation: aiMoreFlow 4s linear infinite;
          transition: transform 0.15s ease, box-shadow 0.15s ease;
        }
        .ai-more-btn:hover {
          transform: translateY(-1px) scale(1.06);
          box-shadow: 0 4px 14px rgba(124, 58, 237, 0.55);
        }
        .ai-more-count {
          background: rgba(255,255,255,0.25);
          padding: 0 6px; border-radius: 8px; font-size: 10px; font-weight: 800;
        }
        @keyframes aiMoreFlow {
          0% { background-position: 0% 50%; }
          100% { background-position: 300% 50%; }
        }
        @keyframes aiBadgePulse {
          0%, 100% { transform: scale(1); box-shadow: 0 2px 8px rgba(124,58,237,0.45); }
          50% { transform: scale(1.08); box-shadow: 0 2px 14px rgba(124,58,237,0.7); }
        }
        @keyframes chatDotPulse {
          0%, 80%, 100% { opacity: 0.3; transform: scale(0.8); }
          40% { opacity: 1; transform: scale(1); }
        }
        .chat-dot {
          display: inline-block;
          width: 7px; height: 7px; border-radius: 50%;
          background: #94a3b8;
          animation: chatDotPulse 1.4s ease-in-out infinite;
        }
      `}</style>
    </>
  );
};

export default ChatBot;
